import { queryOptions, useQuery } from '@tanstack/react-query';
import { requestZeusFromBrowser } from 'components/api/zeusBrowserClient';
import { z } from 'zod';

import { ViewerTourEligibilityDocument } from './__generated__/tours.zeus';
import {
  type AccessStatus,
  BLANK_TOUR_ELIGIBILITY,
  deriveTourEligibility,
  nextRequiredStep,
  type TourEligibility,
} from './tourEligibility';

const TOUR_ELIGIBILITY_QUERY_KEY = ['tours', 'eligibility'] as const;

const knownAccessSchema = z.enum(['VERIFIED', 'UNVERIFIED', 'FLAGGED']);

const viewerStatusSchema = z.object({
  isAuthenticated: z.boolean(),
  accountStatus: z.object({
    access: z.string(),
  }),
  customer: z
    .object({
      primaryPhoneNumber: z.string().nullable(),
    })
    .nullable(),
});

const toAccessStatus = (raw: string): AccessStatus => {
  const parsed = knownAccessSchema.safeParse(raw);
  return parsed.success ? parsed.data : 'UNRECOGNIZED';
};

const tourEligibilityQueryOptions = () =>
  queryOptions({
    queryKey: TOUR_ELIGIBILITY_QUERY_KEY,
    queryFn: async ({ signal }): Promise<TourEligibility> => {
      const data = await requestZeusFromBrowser(ViewerTourEligibilityDocument, {}, signal);
      const viewer = viewerStatusSchema.parse(data.viewer);
      return deriveTourEligibility({
        isAuthenticated: viewer.isAuthenticated,
        access: toAccessStatus(viewer.accountStatus.access),
        // Null means the record carries no number; an unverified-but-present number
        // still counts, because the app's phone step verifies it after booking.
        hasPhoneOnRecord: viewer.customer?.primaryPhoneNumber != null,
      });
    },
    // captureErrorContext is consumed by the QueryCache onError handler built by
    // createMarketplaceQueryCache; a provider without it swallows these errors silently.
    meta: { captureErrorContext: 'ViewerTourEligibility' },
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 0,
  });

export function useTourEligibility({ enabled = true } = {}) {
  const query = useQuery({ ...tourEligibilityQueryOptions(), enabled });

  // Blank eligibility routes to login, the safest default while the viewer is unknown.
  const eligibility = query.data ?? BLANK_TOUR_ELIGIBILITY;

  return {
    eligibility,
    nextStep: nextRequiredStep(eligibility),
    isLoading: query.isPending && query.fetchStatus !== 'idle',
    error: query.error,
    refresh: query.refetch,
  };
}
